import {
  createActivityLogsToken,
  fetchCurrentToken,
  resetActivityLogsToken,
} from "@app/auth";
import { selectEnv } from "@app/config";
import { selectOrganizationSelectedId } from "@app/organizations";
import { useApi, useDispatch, useSelector } from "@app/react";
import { ssoFailureUrl } from "@app/routes";
import { selectAccessToken, selectIsAuthWithSso } from "@app/token";
import { useEffect, useState } from "react";
import {
  Banner,
  BannerMessages,
  Box,
  Button,
  ExternalLink,
  FormGroup,
  Group,
  PreCode,
  Select,
  type SelectOption,
  listToInvertedTextColor,
  tokens,
} from "../shared";

const ssoDurations: SelectOption[] = [
  { label: "1 Hour", value: "3600" },
  { label: "4 Hours", value: "14400" },
  { label: "12 Hours", value: "43200" },
  { label: "24 Hours", value: "86400" },
  { label: "5 Days", value: "432000" },
  { label: "1 Week", value: "604800" },
];

const activityLogsDurations: SelectOption[] = [
  { label: "1 Day", value: "86400" },
  { label: "1 Week", value: "604800" },
  { label: "1 Month", value: "2592000" },
  { label: "3 Months", value: "7776000" },
  { label: "6 Months", value: "15552000" },
  { label: "1 Year", value: "31536000" },
];

const SsoTokenSection = () => {
  const env = useSelector(selectEnv);
  const [dur, setDur] = useState(ssoDurations[0].value);
  const orgId = useSelector(selectOrganizationSelectedId);
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const failure_page = encodeURIComponent(`${env.appUrl}${ssoFailureUrl()}`);
    const url = `${env.authUrl}/organizations/${orgId}/saml/login?landing_page=clitoken&duration=${dur}&redirect_uri=${failure_page}`;
    window.location.replace(url);
  };
  const onSelect = (opt: SelectOption) => setDur(opt.value);

  return (
    <Box>
      <Group>
        <h3 className={tokens.type.h3}>SSO Token for CLI</h3>
        <div>
          To use the{" "}
          <ExternalLink variant="info" href="https://aptible.com/docs/cli">
            Aptible CLI
          </ExternalLink>{" "}
          with SSO, you must generate a new token. The selected lifetime is the
          maximum duration the token will be valid. If your SSO provider is
          configured to use a shorter session duration, that will be applied to
          the token instead. The security best practice is to select the
          shortest lifetime that will meet your needs. You can enforce a maximum
          token duration via your SSO provider (
          <ExternalLink
            variant="info"
            href="https://www.aptible.com/docs/core-concepts/security-compliance/authentication/sso#controlling-cli-token-lifetime"
          >
            see docs
          </ExternalLink>
          ).
        </div>

        <form onSubmit={onSubmit} className="max-w-xs">
          <FormGroup htmlFor="sso-token-duration" label="Token Duration">
            <Select
              options={ssoDurations}
              onSelect={onSelect}
              value={dur}
              ariaLabel="sso-token-duration"
            />
            <Button className="mt-4" type="submit">
              Generate SSO Token
            </Button>
          </FormGroup>
        </form>
      </Group>
    </Box>
  );
};

const ActivityLogsTokenSection = () => {
  const dispatch = useDispatch();
  const [dur, setDur] = useState(
    activityLogsDurations[activityLogsDurations.length - 1].value,
  );
  const [query, setQuery] = useState(
    createActivityLogsToken({ expiresIn: Number.parseInt(dur) }),
  );
  const apiQuery = useApi(query);
  const generatedToken = useSelector((s) => s.activityLogsToken);

  useEffect(() => {
    return () => {
      dispatch(resetActivityLogsToken());
    };
  }, []);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    apiQuery.trigger();
  };

  const onSelect = (opt: SelectOption) => {
    setDur(opt.value);
    setQuery(
      createActivityLogsToken({ expiresIn: Number.parseInt(opt.value) }),
    );
  };

  return (
    <Box>
      <Group>
        <h3 className={tokens.type.h3}>Activity Logs Token</h3>
        <div>
          Generate a long-lived token for accessing activity. This token only
          provides access to activity and other limited personal and
          organization-level metadata. This token can be used with the SIEM or
          other client to retrieve activity logs for your organization.
        </div>

        <BannerMessages {...apiQuery} />

        <form onSubmit={onSubmit} className="max-w-xs">
          <FormGroup
            htmlFor="activity-logs-token-duration"
            label="Token Duration"
          >
            <Select
              options={activityLogsDurations}
              onSelect={onSelect}
              value={dur}
              ariaLabel="activity-logs-token-duration"
            />
            <Button
              className="mt-4"
              type="submit"
              isLoading={apiQuery.isLoading}
            >
              Generate Activity Logs Token
            </Button>
          </FormGroup>
        </form>

        {generatedToken ? (
          <Group className="mt-4">
            <div className="font-semibold">Your Activity Logs Token:</div>
            <Banner variant="info">
              <div className="text-sm">
                Save this token securely. You won't be able to see it again.
              </div>
            </Banner>
            <PreCode
              allowCopy
              segments={listToInvertedTextColor([generatedToken])}
            />
            <div>
              <Button
                variant="white"
                onClick={() => dispatch(resetActivityLogsToken())}
              >
                Dismiss
              </Button>
            </div>
          </Group>
        ) : null}
      </Group>
    </Box>
  );
};

export const SsoTokenCliPage = () => {
  const isAuthWithSso = useSelector(selectIsAuthWithSso);

  return (
    <Group>
      <h2 className={tokens.type.h2}>Access Tokens</h2>

      {isAuthWithSso ? <SsoTokenSection /> : null}

      <ActivityLogsTokenSection />
    </Group>
  );
};

export const SsoTokenCliReadPage = () => {
  const accessToken = useSelector(selectAccessToken);
  const query = useApi(fetchCurrentToken());

  return (
    <Group>
      <h2 className={tokens.type.h2}>Access Tokens</h2>
      <Box>
        <Group>
          <div>
            To use the{" "}
            <ExternalLink variant="info" href="https://aptible.com/docs/cli">
              Aptible CLI
            </ExternalLink>{" "}
            with SSO, enter the below command into your terminal.{" "}
          </div>

          <Group>
            {accessToken === "" && !query.isLoading ? (
              <Banner variant="error">
                <div className="flex gap-2 items-center">
                  Failed to fetch token, try again.{" "}
                  <Button
                    variant="delete"
                    onClick={() => query.trigger()}
                    isLoading={query.isLoading}
                  >
                    Retry
                  </Button>
                </div>
              </Banner>
            ) : null}

            <PreCode
              allowCopy
              segments={listToInvertedTextColor([
                "aptible login --sso",
                accessToken,
              ])}
            />
          </Group>
        </Group>
      </Box>
    </Group>
  );
};
